import React from "react";
import { useHistory } from "react-router-dom";
import {
  defineMessages,
  FormattedMessage as T,
  useIntl,
  IntlShape,
} from "react-intl";
import { makeStyles, Theme } from "@material-ui/core/styles";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import InputAdornment from "@material-ui/core/InputAdornment";
import TextField from "@material-ui/core/TextField";
import Alert, { AlertProps } from "@material-ui/lab/Alert";
import PowerSettingsNewIcon from "@material-ui/icons/PowerSettingsNew";
import RefreshIcon from "@material-ui/icons/Refresh";
import TapAndPlayIcon from "@material-ui/icons/TapAndPlay";
import { AxiosError } from "axios";
import {
  Controller,
  ManualFieldError,
  OnSubmit,
  useForm,
} from "react-hook-form";
import * as yup from "yup";

import { SessionStorageKey, LoginError } from "../AuthProvider";
import FullscreenNotification, {
  FullscreenNotificationProps,
} from "../FullscreenNotification";
import useAuth from "../../hooks/useAuth";
import useSocketIo from "../../hooks/useSocketIo";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: "100%",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  lightLoginForm: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
  },
  tapAndPlayAvatar: {
    width: 96,
    height: 96,
    position: "absolute",
    top: -(24 + 48),
    backgroundColor: theme.palette.getContrastText(theme.palette.primary.main),
    color: theme.palette.primary.main,
    boxShadow: theme.shadows[1],
  },
  tapAndPlayIcon: {
    width: 64,
    height: 64,
  },
  textField: {
    marginTop: 16,
  },
  lightLoginFooter: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 16,
  },
  lightLoginFooterAlert: {
    width: "100%",
  },
  lightLoginFooterActions: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    marginTop: 16,
    "& button:last-child": {
      marginLeft: 16,
    },
  },
}));

const msg = defineMessages({
  cardUidLabel: {
    id: "lightLoginForm.cardUid.label",
    description: "Message displayed to labeled the cardUid field.",
    defaultMessage: "Card UID",
  },
  cardUidRequired: {
    id: "lightLoginForm.cardUid.required",
    description: "Message displayed when the card UID field is empty.",
    defaultMessage: "The card UID is required",
  },
  deviceIdLabel: {
    id: "lightLoginForm.deviceId.label",
    description: "Message displayed to labeled the deviceId field.",
    defaultMessage: "Device ID",
  },
  deviceIdRequired: {
    id: "lightLoginForm.deviceId.required",
    description: "Message displayed when the device ID field is empty.",
    defaultMessage: "The device ID is required",
  },
  logoutButtonLabel: {
    id: "lightLoginForm.logoutButton.label",
    description: "Label of the logout button.",
    defaultMessage: "Log out",
  },
  submitButtonLabel: {
    id: "lightLoginForm.submitButton.label",
    description: "Label of the submit button.",
    defaultMessage: "Log back",
  },
  nfcReaderOffline: {
    id: "lightLoginForm.nfcReader.offline",
    description: "Message displayed when the NFC reader is offline.",
    defaultMessage: "NFC reader offline",
  },
  nfcReaderWaiting: {
    id: "lightLoginForm.nfcReader.waiting",
    description: "Message displayed when the NFC reader is waiting.",
    defaultMessage: "NFC reader waiting",
  },
  nfcScanRequired: {
    id: "lightLoginForm.nfcScan.required",
    description: "Message displayed when the NFC scan is required.",
    defaultMessage: "NFC scan required",
  },
  nfcScanSuccess: {
    id: "lightLoginForm.nfcScan.success",
    description: "Message displayed when the NFC scan is successful.",
    defaultMessage: "NFC scan successful",
  },
  [LoginError.INVSN]: {
    id: "lightLoginForm.loginError.invsn",
    description: "Message displayed when the device ID is invalid.",
    defaultMessage: "The device ID is invalid",
  },
  [LoginError.LOG]: {
    id: "lightLoginForm.loginError.log",
    description: "Message displayed when the matricule is invalid.",
    defaultMessage: "The matricule is invalid",
  },
  [LoginError.MP]: {
    id: "lightLoginForm.loginError.mp",
    description:
      "Message displayed when the matricule and/or the pin code is missing.",
    defaultMessage: "The matricule and/or the pin code is missing",
  },
  [LoginError.MSN]: {
    id: "lightLoginForm.loginError.msn",
    description: "Message displayed when the device ID is missing.",
    defaultMessage: "The device ID is missing",
  },
  [LoginError.PASS]: {
    id: "lightLoginForm.loginError.pass",
    description: "Message displayed when the pin code is invalid.",
    defaultMessage: "The pin code is invalid",
  },
  [LoginError.UN1]: {
    id: "lightLoginForm.loginError.un1",
    description: "Message displayed when the user is unauthorized.",
    defaultMessage: "Unauthorized",
  },
  [LoginError.UN2]: {
    id: "lightLoginForm.loginError.un2",
    description: "Message displayed when the user is unauthorized.",
    defaultMessage: "Unauthorized",
  },
  [LoginError.UN3]: {
    id: "lightLoginForm.loginError.un3",
    description: "Message displayed when the user is unauthorized.",
    defaultMessage: "Unauthorized",
  },
  loginErrorUnknown: {
    id: "lightLoginForm.loginError.unknown",
    description: "Message displayed when the login error is unknown.",
    defaultMessage: "Unknown login error",
  },
  loginErrorLoggedOutIn: {
    id: "lightLoginForm.loginError.loggedOutIn",
    description:
      "Message displayed when there is an unrecoverable login error and the user will be logged out.",
    defaultMessage: "You will be logged out in a few seconds...",
  },
});

interface NfcScanEventData {
  readerName: string;
  deviceId: string;
  uid: string;
}

function getLoginFormSchema(y: typeof yup, t: IntlShape["formatMessage"]) {
  return y.object().shape({
    cardUid: y.string().required(t(msg.cardUidRequired)),
    deviceId: y.string().required(t(msg.deviceIdRequired)),
    matricule: y.string().required(),
    pinCode: y.string().required(),
  });
}

export type LoginFormValues = yup.InferType<
  ReturnType<typeof getLoginFormSchema>
>;

const emptyLoginFormValues: LoginFormValues = {
  cardUid: "",
  deviceId: "",
  matricule: "",
  pinCode: "",
};

type LightLoginFormProps = {
  onSubmitSuccess?: OnSubmit<LoginFormValues>;
};

function LightLoginForm(props: LightLoginFormProps) {
  const { onSubmitSuccess } = props;
  const classes = useStyles();
  const history = useHistory();
  const { formatMessage: t } = useIntl();
  const {
    control,
    errors,
    formState,
    handleSubmit,
    register,
    setError,
    setValue,
    triggerValidation,
    unregister,
  } = useForm<LoginFormValues>({
    defaultValues: emptyLoginFormValues,
    validationSchema: getLoginFormSchema(yup, t),
  });
  const { authenticationService, login, logout } = useAuth();
  const [io, connected] = useSocketIo("http://localhost:4001");
  const [loading, setLoading] = React.useState(false);
  const [notification, setNotification] =
    React.useState<FullscreenNotificationProps>({ open: false });

  // Register the user credentials fields as they are not in the markup.
  React.useEffect(() => {
    register({ name: "matricule" });
    register({ name: "pinCode" });

    return () => {
      unregister(["matricule", "pinCode"]);
    };
  }, [register, unregister]);

  // Fill the user credentials fields.
  React.useEffect(() => {
    const { matricule, pinCode } = authenticationService.getUserCredentials();

    if (matricule === null) {
      if (process.env.NODE_ENV !== "production") {
        console.error(
          `No matricule found in the session storage with the following key: "${SessionStorageKey.Matricule}".`
        );
      }
      logout("hard");
      return;
    }

    if (pinCode === null) {
      if (process.env.NODE_ENV !== "production") {
        console.error(
          `No pin code found in the session storage with the following key: "${SessionStorageKey.PinCode}".`
        );
      }
      logout("hard");
      return;
    }

    setValue("matricule", matricule);
    setValue("pinCode", pinCode);
  }, [authenticationService, history, logout, setValue]);

  // Binds NFC scan events via socket channel.
  React.useEffect(() => {
    io.off("nfc.scan");
    io.on("nfc.scan", (data: NfcScanEventData) => {
      setValue("cardUid", data.uid);
      setValue("deviceId", data.deviceId);
      triggerValidation(["cardUid", "deviceId"]);
    });
  }, [io, setValue, triggerValidation]);

  /**
   * Helper message to display in the login footer (mostly NFC related).
   */
  const helperText: AlertProps = React.useMemo(() => {
    const { dirtyFields } = formState;

    const nfcReaderOffline = !connected;
    const nfcScanError = !!errors.cardUid || !!errors.deviceId;
    const nfcReaderWaiting =
      !dirtyFields.has("cardUid") || !dirtyFields.has("deviceId");
    const nfcScanSuccess = !nfcScanError && !nfcReaderWaiting;

    if (nfcReaderOffline && !nfcScanSuccess) {
      return { severity: "warning", children: <T {...msg.nfcReaderOffline} /> };
    }

    if (!!errors.cardUid) {
      if (errors.cardUid.isManual) {
        return { severity: "error", children: errors.cardUid.message };
      }

      return { severity: "error", children: <T {...msg.nfcScanRequired} /> };
    }

    if (!!errors.deviceId) {
      if (errors.deviceId.isManual) {
        return { severity: "error", children: errors.deviceId.message };
      }

      return { severity: "error", children: <T {...msg.nfcScanRequired} /> };
    }

    if (nfcReaderWaiting) {
      return { severity: "info", children: <T {...msg.nfcReaderWaiting} /> };
    }

    return { severity: "success", children: <T {...msg.nfcScanSuccess} /> };
  }, [connected, errors.cardUid, errors.deviceId, formState]);

  const handleLogoutButtonClick = React.useCallback(
    (_event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      logout("hard");
    },
    [logout]
  );

  /**
   * Takes care of a login error.
   */
  const processLoginError = React.useCallback(
    (error: AxiosError) => {
      let fieldErrors: ManualFieldError<LoginFormValues>[] | undefined;
      let loginErrorMessage: FullscreenNotificationProps["message"] | undefined;

      switch (error.response?.data.code) {
        case LoginError.INVSN:
          fieldErrors = [
            {
              name: "deviceId",
              type: "loginError",
              message: <T {...msg[LoginError.INVSN]} />,
            },
          ];
          break;
        case LoginError.LOG:
          fieldErrors = [
            {
              name: "matricule",
              type: "loginError",
              message: <T {...msg[LoginError.LOG]} />,
            },
          ];
          loginErrorMessage = (
            <>
              <T {...msg[LoginError.LOG]} />
              {". "}
              <T {...msg.loginErrorLoggedOutIn} />
            </>
          );
          break;
        case LoginError.MP:
          fieldErrors = [
            {
              name: "matricule",
              type: "loginError",
              message: <T {...msg[LoginError.MP]} />,
            },
            {
              name: "pinCode",
              type: "loginError",
              message: <T {...msg[LoginError.MP]} />,
            },
          ];
          loginErrorMessage = (
            <>
              <T {...msg[LoginError.MP]} />
              {". "}
              <T {...msg.loginErrorLoggedOutIn} />
            </>
          );
          break;
        case LoginError.MSN:
          fieldErrors = [
            {
              name: "deviceId",
              type: "loginError",
              message: <T {...msg[LoginError.MSN]} />,
            },
          ];
          break;
        case LoginError.PASS:
          fieldErrors = [
            {
              name: "pinCode",
              type: "loginError",
              message: <T {...msg[LoginError.PASS]} />,
            },
          ];
          loginErrorMessage = (
            <>
              <T {...msg[LoginError.PASS]} />
              {". "}
              <T {...msg.loginErrorLoggedOutIn} />
            </>
          );
          break;
        case LoginError.UN1:
          loginErrorMessage = (
            <>
              <T {...msg[LoginError.UN1]} />
              {". "}
              <T {...msg.loginErrorLoggedOutIn} />
            </>
          );
          break;
        case LoginError.UN2:
          loginErrorMessage = (
            <>
              <T {...msg[LoginError.UN2]} />
              {". "}
              <T {...msg.loginErrorLoggedOutIn} />
            </>
          );
          break;
        case LoginError.UN3:
          loginErrorMessage = (
            <>
              <T {...msg[LoginError.UN3]} />
              {". "}
              <T {...msg.loginErrorLoggedOutIn} />
            </>
          );
          break;
        default:
          loginErrorMessage = (
            <>
              <T {...msg.loginErrorUnknown} />
              {". "}
              <T {...msg.loginErrorLoggedOutIn} />
            </>
          );
          break;
      }

      if (fieldErrors !== undefined) setError(fieldErrors);
      if (loginErrorMessage !== undefined) {
        setNotification({
          open: true,
          message: loginErrorMessage,
          type: "error",
        });
        setTimeout(() => logout("hard"), 3000);
      }
    },
    [logout, setError]
  );

  /**
   * Takes care of the submit process.
   */
  const handleLightLoginFormSubmit = React.useCallback(
    (data: LoginFormValues, event?: React.BaseSyntheticEvent) => {
      setLoading(true);

      login(data.matricule, data.pinCode, data.deviceId, data.cardUid, "1")
        .then((_ujwt: string) => {
          if (onSubmitSuccess) onSubmitSuccess(data, event);
        })
        .catch(processLoginError)
        .finally(() => setLoading(false));
    },
    [login, onSubmitSuccess, processLoginError]
  );

  return (
    <div className={classes.root}>
      <form
        className={classes.lightLoginForm}
        onSubmit={handleSubmit(handleLightLoginFormSubmit)}
      >
        <Avatar className={classes.tapAndPlayAvatar}>
          <TapAndPlayIcon className={classes.tapAndPlayIcon} />
        </Avatar>
        <Controller
          name="cardUid"
          as={
            <TextField
              className={classes.textField}
              helperText={!!errors.cardUid && errors.cardUid.message}
              label={t(msg.cardUidLabel)}
              required={errors.cardUid?.type === "required"}
              error={!!errors.cardUid}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <TapAndPlayIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          }
          control={control}
        />
        <Controller
          name="deviceId"
          as={
            <TextField
              className={classes.textField}
              helperText={!!errors.deviceId && errors.deviceId.message}
              label={t(msg.deviceIdLabel)}
              required={errors.deviceId?.type === "required"}
              error={!!errors.deviceId}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <TapAndPlayIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          }
          control={control}
        />
        <div className={classes.lightLoginFooter}>
          <Alert className={classes.lightLoginFooterAlert} {...helperText} />
          <div className={classes.lightLoginFooterActions}>
            <Button
              size="small"
              variant="contained"
              startIcon={<PowerSettingsNewIcon />}
              disabled={formState.isSubmitting || loading}
              onClick={handleLogoutButtonClick}
            >
              <T {...msg.logoutButtonLabel} />
            </Button>
            <Button
              color="primary"
              variant="contained"
              type="submit"
              startIcon={
                formState.isSubmitting || loading ? (
                  <CircularProgress size={20} />
                ) : (
                  <RefreshIcon />
                )
              }
              disabled={formState.isSubmitting || loading}
            >
              <T {...msg.submitButtonLabel} />
            </Button>
          </div>
        </div>
      </form>
      <FullscreenNotification {...notification} />
    </div>
  );
}

export default LightLoginForm;
